/**
 * BANK-IBAN-R3.4 — customer Mini App bank-transfer launch + copy helpers.
 * Opening a bank app is NOT paid. Copy is NOT paid. Only AsiaStyle status poll is paid.
 */
import { openHttpsSync, isMobileExternalActionPlatform } from "./external-link-v2.js";
import { fetchOrderDetailBounded } from "./order-history-v2.js";
import { isOrderPaidStatus } from "./order-status-copy-v2.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";
import { fetchWithTimeout } from "./fetch-timeout-v2.js";
import { waitForTelegramInitData } from "../lib/telegram.js";
import { STORE_INFO } from "./store-merchant-sot-v2.js";

export type BankPaymentClientPayload = {
  public_order_number: string;
  payment_method: string;
  payment_rail: string;
  amount_minor: number;
  amount_uah: string;
  currency: string;
  seller: string;
  rnokpp: string;
  bank: string;
  bank_legal?: string;
  iban: string;
  iban_display: string;
  purpose: string;
  public_url: string;
  launch: {
    kind: string;
    enabled: boolean;
    label_uk: string;
    uri: string | null;
    reason?: string;
  };
  awaiting_status: string;
};

export function parseBankPaymentPayload(raw: unknown): BankPaymentClientPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const publicOrder = String(o.public_order_number ?? "").trim();
  const purpose = String(o.purpose ?? "").trim();
  const iban = String(o.iban ?? "").replace(/\s+/g, "").toUpperCase();
  const amountMinor = Number(o.amount_minor);
  if (!/^STG-\d+$/i.test(publicOrder)) return null;
  if (/ord_/i.test(purpose) || /ord_/i.test(publicOrder)) return null;
  if (iban !== STORE_INFO.iban) return null;
  if (!Number.isInteger(amountMinor) || amountMinor <= 0) return null;
  if (purpose !== getBankPaymentPurpose(publicOrder)) return null;
  const launch = o.launch && typeof o.launch === "object" ? (o.launch as Record<string, unknown>) : {};
  const uri = launch.uri == null ? null : String(launch.uri);
  if (uri && !/^https:\/\/(bank\.gov\.ua\/qr\/|qr\.bank\.gov\.ua\/)/.test(uri)) return null;
  if (uri && /privat24|liqpay|novapay|oplata/i.test(uri)) return null;
  return {
    public_order_number: publicOrder,
    payment_method: String(o.payment_method ?? "bank_transfer_fop"),
    payment_rail: String(o.payment_rail ?? "BANK_IBAN_DIRECT_TRANSFER"),
    amount_minor: amountMinor,
    amount_uah: String(o.amount_uah ?? ""),
    currency: String(o.currency ?? "UAH"),
    seller: String(o.seller ?? ""),
    rnokpp: String(o.rnokpp ?? ""),
    bank: String(o.bank ?? "ПриватБанк"),
    ...(o.bank_legal ? { bank_legal: String(o.bank_legal) } : {}),
    iban,
    iban_display: String(o.iban_display ?? iban),
    purpose,
    public_url: String(o.public_url ?? ""),
    launch: {
      kind: String(launch.kind ?? "deeplink"),
      enabled: launch.enabled !== false,
      label_uk: String(launch.label_uk ?? "Оплатити через банк"),
      uri,
      ...(launch.reason ? { reason: String(launch.reason) } : {}),
    },
    awaiting_status: String(o.awaiting_status ?? "AWAITING_BANK_TRANSFER"),
  };
}

export function getBankPaymentPurpose(publicOrderNumber: string | null | undefined): string | null {
  const raw = String(publicOrderNumber ?? "").trim();
  if (!/^STG-\d+$/i.test(raw)) return null;
  const n = raw.replace(/^stg-/i, "STG-");
  return `Оплата за замовлення ${n}`;
}

export type BankPaymentRequisites = {
  amount: string;
  recipient: string;
  taxId: string;
  bankName: string;
  iban: string;
  purpose: string;
};

export function shouldAttemptNbuBankDeeplink(): boolean {
  try {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const platform = (window as unknown as { Telegram?: { WebApp?: { platform?: string } } }).Telegram
      ?.WebApp?.platform;
    return isMobileExternalActionPlatform(ua, platform);
  } catch {
    return false;
  }
}

export function attemptBankDeeplink(uri: string | null | undefined): {
  attempted: boolean;
  opened: boolean;
  reason: string;
} {
  const value = String(uri ?? "").trim();
  if (!value) return { attempted: false, opened: false, reason: "missing_uri" };
  if (!shouldAttemptNbuBankDeeplink()) {
    return { attempted: false, opened: false, reason: "desktop_or_unsupported" };
  }
  const opened = openHttpsSync(value);
  return {
    attempted: true,
    opened: opened.kind === "opened",
    reason: opened.kind === "opened" ? opened.via : "open_failed",
  };
}

export function bankCopyFields(payload: BankPaymentClientPayload): Record<string, string> {
  return {
    amount: payload.amount_uah,
    iban: payload.iban,
    purpose: payload.purpose,
    seller: payload.seller,
    rnokpp: payload.rnokpp,
    bank: payload.bank,
  };
}

export function bankCopyAllBlock(payload: BankPaymentClientPayload): string {
  return [
    `Замовлення: ${payload.public_order_number}`,
    `Сума: ${payload.amount_uah} ₴`,
    `Отримувач: ${payload.seller}`,
    `РНОКПП / ІПН: ${payload.rnokpp}`,
    `Банк: ${payload.bank}`,
    `IBAN: ${payload.iban_display}`,
    `Призначення платежу: ${payload.purpose}`,
  ].join("\n");
}

export async function fetchBankInstructionV2(input: {
  sessionKey: string;
  orderId: string;
}): Promise<BankPaymentClientPayload | null> {
  await waitForTelegramInitData(2500);
  const qs = new URLSearchParams({ session_key: input.sessionKey });
  const res = await fetchWithTimeout(
    `/api/store-v2/bank-instruction?${qs}&order_id=${encodeURIComponent(input.orderId)}`,
    { method: "GET", headers: storeV2AuthHeaders() },
    15_000,
  );
  const json = (await res.json()) as { ok?: boolean; bank_payment?: unknown };
  if (!res.ok || json.ok === false) return null;
  return parseBankPaymentPayload(json.bank_payment);
}

export async function pollAsiaStyleBankPaymentStatus(input: {
  sessionKey: string;
  orderId: string;
}): Promise<{
  paid: boolean;
  payment_status: string | null;
  created_at: string | null;
  bank_confirmed_at: string | null;
}> {
  const detail = await fetchOrderDetailBounded(input.sessionKey, input.orderId);
  if (!detail.ok || !detail.data) {
    return { paid: false, payment_status: null, created_at: null, bank_confirmed_at: null };
  }
  const order = detail.data.order;
  const status = String(order.payment_status ?? "");
  return {
    paid: isOrderPaidStatus(status),
    payment_status: status,
    created_at: order.created_at ? String(order.created_at) : null,
    bank_confirmed_at: order.bank_confirmed_at ? String(order.bank_confirmed_at) : null,
  };
}

/** K21 — bounded resume checks after return from the bank app. Never infinite. */
export const BANK_RESUME_POLL_MS = [0, 2_000, 5_000, 10_000, 20_000] as const;

type TelegramOpenLink = {
  openLink?: (url: string) => void;
};

/** K14/K20 — open NBU UCT HTTPS only after STG exists. Never invent a URI. Do not navigate the Mini App away. */
export function tryOpenNbuBankApp(uri: string | null | undefined): boolean {
  const href = String(uri ?? "").trim();
  if (!/^https:\/\/(bank\.gov\.ua\/qr\/|qr\.bank\.gov\.ua\/)/i.test(href)) return false;
  try {
    const tg = (window as unknown as { Telegram?: { WebApp?: TelegramOpenLink } }).Telegram?.WebApp;
    if (typeof tg?.openLink === "function") {
      tg.openLink(href);
      return true;
    }
    const opened = window.open(href, "_blank", "noopener,noreferrer");
    if (opened) return true;
    const a = document.createElement("a");
    a.href = href;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  } catch {
    return false;
  }
}
